import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, useWindowDimensions, View } from "react-native";
import Svg, { Path } from "react-native-svg";

import { DashboardProgramCard } from "../../../components/student/DashboardProgramCard";
import { EditorialIntro } from "../../../components/aspects/EditorialIntro";
import { DueSoonRail, type DueSoonRailItem } from "../../../components/aspects/DueSoonRail";
import { SectionHeader } from "../../../components/aspects/SectionHeader";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { BrandScene } from "../../../components/student/BrandScene";
import { BrandGlow } from "../../../components/student/BrandGlow";
import { BrandEntrance } from "../../../components/student/BrandEntrance";
import { clearProgramEntranceFlags } from "../../../components/student/ProgramEntrance";
import { StudentPortalShell, type StudentPortalIdentity } from "../../../components/student/StudentPortalShell";
import { EmptyState, Skeleton } from "../../../components/ui";
import { useAuth } from "../../../lib/authContext";
import { formatDateShort } from "../../../lib/datetime";
import {
  countRegisteredPrograms,
  listActiveRegisteredCompetitions,
  listUpcomingDeadlines,
  type DashboardDeadlineRow,
} from "../../../lib/studentDashboard";
import { listMyCareerInterests, type StudentCareerInterest } from "../../../lib/positions";
import { countMyPendingInterviewOffers } from "../../../lib/interviewOffers";
import { listMyDisbursements, formatUsd, type StudentDisbursementRow } from "../../../lib/winners";
import {
  firstNameFromDisplay,
  studentPortalRoleLine,
} from "../../../lib/studentPortalUi";
import { getCachedShellSnapshot, updateCachedShellSnapshot } from "../../../lib/useStudentPortalShellData";
import { countActiveTeamsForStudent } from "../../../lib/studentTeams";
import { getMyStudent } from "../../../lib/students";
import { supabase } from "../../../lib/supabase";
import type { Student, StudentCaseRow } from "../../../types/database";

// Dark-section tokens (forest below the hero divider)
const DARK_TEXT = COLORS.LINEN.hex;
const DARK_TEXT_MUTED = "rgba(247,244,238,0.62)";
const DARK_BORDER = "rgba(247,244,238,0.08)";
const DARK_DIVIDER = "rgba(247,244,238,0.06)";

/**
 * v2 student top-level dashboard.
 *
 * Layout (per `.devlog/portal-structure-v2.md` §2a):
 *   - Persistent header at top (logo + Activity / Settings / Profile
 *     overlays). The shell renders this; we just opt out of the green
 *     sidebar with `hideSidebar`.
 *   - Big gold "Find Competition" button at the top of the page.
 *   - Two-column body on wide screens (stacked on narrow):
 *       Left  → "My Programs" — one card per enrolled program.
 *       Right → "Upcoming Deliverables" — `Program — Deliverable: Date`
 *               list, sorted by soonest.
 *   - Profile / activity / settings live in the header overlays — not
 *     on this page.
 */
const STUDENT_THEME = PANE_THEMES.Student;

export default function StudentPortalScreen() {
  const { user } = useAuth();
  const { width } = useWindowDimensions();
  const wide = width >= 900;

  const [student, setStudent] = useState<Student | null | undefined>(undefined);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [registeredPrograms, setRegisteredPrograms] = useState<number>(
    () => getCachedShellSnapshot(user?.id)?.casesBadgeCount ?? 0
  );
  const [activeCases, setActiveCases] = useState<StudentCaseRow[]>([]);
  const [deadlines, setDeadlines] = useState<DashboardDeadlineRow[]>([]);
  const [careerInterests, setCareerInterests] = useState<StudentCareerInterest[]>([]);
  const [pendingOffers, setPendingOffers] = useState(0);
  const [winnings, setWinnings] = useState<StudentDisbursementRow[]>([]);
  // One-time cursive grad message: null when never dismissed, ISO string when dismissed.
  const [welcomeGradSeenAt, setWelcomeGradSeenAt] = useState<string | null>(null);

  const hasLoadedRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      hasLoadedRef.current = null;
      setStudent(null);
      setDisplayName("");
      setAvatarUrl(null);
      setRegisteredPrograms(0);
      setActiveCases([]);
      setDeadlines([]);
      setLoading(false);
      return;
    }

    const uid = user.id;
    if (hasLoadedRef.current !== uid) setLoading(true);
    try {
      const [
        sRow,
        profileRes,
        regCount,
        teamsCount,
        active,
        dlines,
      ] = await Promise.all([
        getMyStudent(),
        supabase
          .from("user_profiles")
          .select("full_name, avatar_url, welcome_grad_message_seen_at")
          .eq("id", uid)
          .maybeSingle(),
        countRegisteredPrograms(uid),
        countActiveTeamsForStudent(uid),
        listActiveRegisteredCompetitions(uid),
        listUpcomingDeadlines(uid, 20),
      ]);
      // Career interests + pending interview-offer count + active
      // winnings are fire-and-forget — none should block the rest of
      // the dashboard from rendering.
      void listMyCareerInterests().then((rows) => setCareerInterests(rows));
      void countMyPendingInterviewOffers().then((n) => setPendingOffers(n));
      void listMyDisbursements().then((rows) => setWinnings(rows));

      setStudent(sRow as Student | null);

      const fromProfile =
        typeof profileRes.data?.full_name === "string" ? profileRes.data.full_name.trim() : "";
      const fromMeta =
        typeof user.user_metadata?.full_name === "string"
          ? String(user.user_metadata.full_name).trim()
          : "";
      setDisplayName(fromProfile || fromMeta || user.email || "");

      if (typeof profileRes.data?.avatar_url === "string" && profileRes.data.avatar_url.trim()) {
        setAvatarUrl(profileRes.data.avatar_url);
      } else {
        setAvatarUrl((user.user_metadata?.avatar_url as string | undefined) ?? null);
      }

      const seenAt = (profileRes.data as { welcome_grad_message_seen_at?: string | null } | null)
        ?.welcome_grad_message_seen_at ?? null;
      setWelcomeGradSeenAt(seenAt);

      setRegisteredPrograms(regCount);
      updateCachedShellSnapshot(uid, { casesBadgeCount: regCount, teamsBadgeCount: teamsCount });
      setActiveCases(active);
      setDeadlines(dlines);
    } finally {
      hasLoadedRef.current = uid;
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Re-fetch every time the student lands back on /student. Without this the
  // "Upcoming deliverables" rail keeps showing rows for things the team has
  // already submitted (the home page would otherwise only refetch on a hard
  // reload, since the route stays mounted across in-app navigations).
  // Also wipes the per-program entrance flags so the next workspace
  // visit replays the loading cover — visiting home is the signal that
  // the student has fully left whatever program they were in.
  useFocusEffect(
    useCallback(() => {
      void refresh();
      clearProgramEntranceFlags();
    }, [refresh])
  );

  // First-login redirect: on web, send brand-new students to /welcome once.
  useEffect(() => {
    if (Platform.OS !== "web" || !user) return;
    try {
      if (typeof window !== "undefined" && window.localStorage?.getItem("coravu.welcomeSeen.v1") !== "1") {
        router.replace("/welcome" as Href);
      }
    } catch {
      // localStorage unavailable (private mode) — skip the redirect.
    }
  }, [user]);

  const firstName = firstNameFromDisplay(displayName);

  const daysUntilGraduation = useMemo(() => {
    if (!student?.graduation_year || typeof student.graduation_year !== "number") return null;
    return daysUntilGraduationDate(student.graduation_year, student.graduation_term ?? null);
  }, [student?.graduation_year, student?.graduation_term]);

  // The cursive grad message would normally show on the very first
  // /student visit after signup (when welcomeGradSeenAt is null and we
  // know the grad year). Disabled for now per direction — we may turn
  // it back on later, so the dismiss handler + persistence + grad-day
  // calc all stay in place. Flip this back to the predicate to re-enable.
  const showGradMessage = false;

  const dismissGradMessage = useCallback(async () => {
    if (!user) return;
    // Optimistic — hide immediately, then persist. If the write fails we
    // log but don't reopen; the user clearly wants it gone.
    setWelcomeGradSeenAt(new Date().toISOString());
    const { error } = await supabase
      .from("user_profiles")
      .update({ welcome_grad_message_seen_at: new Date().toISOString() })
      .eq("id", user.id);
    if (error) console.warn("[student/index] dismiss grad message:", error.message);
  }, [user]);

  const identity: StudentPortalIdentity | null = useMemo(() => {
    if (!displayName) return null;
    return {
      fullName: displayName,
      subtitleLine: studentPortalRoleLine(student ?? null),
    };
  }, [displayName, student]);

  if (loading || student === undefined) {
    return (
      <>
        <StudentPortalShell
        topbarTitle="Home"
        topbarFirstName={firstNameFromDisplay(displayName || "…")}
        avatarName={displayName || "Student"}
        avatarUrl={avatarUrl}
        identity={identity}
        casesBadgeCount={registeredPrograms}
        hideSidebar
        >
          <DashboardSkeleton wide={wide} />
        </StudentPortalShell>
        <BrandEntrance />
      </>
    );
  }

  return (
    <>
    <StudentPortalShell
      topbarTitle="Home"
      topbarFirstName={firstName}
      avatarName={displayName}
      avatarUrl={avatarUrl}
      identity={identity}
      casesBadgeCount={registeredPrograms}
      hideSidebar
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        style={{ backgroundColor: COLORS.FOREST.hex }}
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <BrandGlow variant="warm" />
        {/* Editorial hero — two-column on wide viewports:
            left = greeting + grad message + Find Competition CTA,
            right = Upcoming Deliverables editorial sidebar on beige.
            Branches live INSIDE the hero so they read against the
            beige bg (see the hero's own `<BrandScene scene="palms" tone="on-beige" />`). */}
        <DashboardHero
          firstName={firstName}
          onFind={() => router.push("/student/find" as Href)}
          showGradMessage={showGradMessage}
          daysUntilGraduation={daysUntilGraduation}
          onDismissGrad={() => void dismissGradMessage()}
          deadlines={deadlines}
          wide={wide}
        />

        {/* Forest-green section — everything below the hero divider.
            The amber hairline above (in DashboardHero) is the visual
            seam between cream + green. The branch tone here is
            offset so the green's top palm appears to come UP from the
            seam, continuing the line of the beige's bottom palm. */}
        <View
          style={{
            backgroundColor: COLORS.FOREST.hex,
            // Stretch the green floor past the cards so the page
            // doesn't revert to sand at the bottom on tall viewports.
            paddingBottom: 60,
            minHeight: 600,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <BrandScene scene="palms" tone="on-green" />
          <View className="max-w-[1200px] mx-auto px-5 pt-7 pb-6 md:px-11">
            {/* My Programs — full content width now that Upcoming
                Deliverables has moved up into the hero. Header row
                shows the section label on the left + the active-count
                / season subtitle on the right. */}
            <ProgramsHeaderRow cases={activeCases} />
            {activeCases.length === 0 ? (
              <View
                style={[
                  {
                    backgroundColor: COLORS.LINEN.hex,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: "rgba(28,43,26,0.10)",
                    paddingHorizontal: 24,
                    paddingVertical: 36,
                    alignItems: "center",
                  },
                  Platform.OS === "web"
                    ? ({ boxShadow: "0 2px 6px rgba(0,0,0,0.18)" } as object)
                    : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.displayMedium,
                    fontSize: 22,
                    color: COLORS.FOREST.hex,
                    marginBottom: 6,
                  }}
                >
                  No programs yet.
                </Text>
                <Text
                  style={{
                    textAlign: "center",
                    fontFamily: FONTS.body,
                    fontSize: 12.5,
                    color: STUDENT_THEME.textOnSurfaceDim,
                    maxWidth: 420,
                    lineHeight: 18,
                  }}
                >
                  Use Find a competition above to discover open programs and register.
                </Text>
              </View>
            ) : (
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  // Margin trick instead of `gap` so each child can use
                  // an integer percentage width without subtracting the
                  // gap (which RN's StyleSheet doesn't support via calc).
                  marginHorizontal: wide ? -8 : 0,
                }}
              >
                {activeCases.map((c) => (
                  <View
                    key={c.id}
                    style={{
                      width: wide ? "50%" : "100%",
                      paddingHorizontal: wide ? 8 : 0,
                      paddingBottom: wide ? 16 : 14,
                    }}
                  >
                    <DashboardProgramCard
                      row={c}
                      accent={c.status === "judging" ? "judging" : "active"}
                      theme="dark"
                    />
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </StudentPortalShell>
    <BrandEntrance />
    </>
  );
}

/**
 * 3-up tile row below My Programs in the green section. Each child
 * tile self-hides when empty, so this row can render 0–3 tiles wide;
 * on narrow viewports they stack vertically.
 *
 * The row also strips the per-tile `marginTop` (the tiles were
 * originally laid out vertically in a rail; here they're column
 * children whose spacing is owned by the row's `gap`).
 */
function AuxiliaryTilesRow({
  winnings,
  pendingOffers,
  careerInterests,
  wide,
}: {
  winnings: StudentDisbursementRow[];
  pendingOffers: number;
  careerInterests: StudentCareerInterest[];
  wide: boolean;
}) {
  // Each tile is self-hiding when empty; if all three would hide, skip
  // the whole row to avoid leaving a `marginTop: 24` ghost gap.
  const showWinnings =
    winnings.some((r) => r.status === "pending_tax_info" || r.status === "ready_to_pay") ||
    winnings.some(
      (r) => r.status === "paid" && r.paid_at && Date.now() - new Date(r.paid_at).getTime() < 30 * 86400 * 1000
    );
  const showOffers = pendingOffers > 0;
  const showCareer = careerInterests.length > 0;
  if (!showWinnings && !showOffers && !showCareer) return null;

  return (
    <View
      style={{
        marginTop: 24,
        flexDirection: wide ? "row" : "column",
        gap: wide ? 14 : 18,
        alignItems: "stretch",
        flexWrap: wide ? "wrap" : "nowrap",
      }}
    >
      {showWinnings ? (
        <View style={{ flexBasis: 0, flexGrow: 1, minWidth: wide ? 240 : 0, width: wide ? undefined : "100%" }}>
          <WinningsTile winnings={winnings} theme="dark" />
        </View>
      ) : null}
      {showOffers ? (
        <View style={{ flexBasis: 0, flexGrow: 1, minWidth: wide ? 240 : 0, width: wide ? undefined : "100%" }}>
          <InterviewOffersTile pendingCount={pendingOffers} theme="dark" />
        </View>
      ) : null}
      {showCareer ? (
        <View style={{ flexBasis: 0, flexGrow: 1, minWidth: wide ? 240 : 0, width: wide ? undefined : "100%" }}>
          <CareerInterestsTile interests={careerInterests} theme="dark" />
        </View>
      ) : null}
    </View>
  );
}

function WinningsTile({
  winnings,
  theme = "light",
}: {
  winnings: StudentDisbursementRow[];
  theme?: "light" | "dark";
}) {
  const isDark = theme === "dark";
  const titleColor = COLORS.FOREST.hex;
  const subColor = STUDENT_THEME.textOnSurfaceDim;
  const cardBg = COLORS.LINEN.hex;
  const cardBorder = COLORS.AMBER.hex;
  // Show only when the student has at least one disbursement that's
  // either still in-flight (pending W-9 / ready_to_pay) OR was paid
  // recently (last 30 days). The page itself shows the full history.
  const active = winnings.filter(
    (r) => r.status === "pending_tax_info" || r.status === "ready_to_pay"
  );
  const recentPaid = winnings.filter(
    (r) => r.status === "paid" && r.paid_at && Date.now() - new Date(r.paid_at).getTime() < 30 * 86400 * 1000
  );
  if (active.length === 0 && recentPaid.length === 0) return null;

  // Lead with the most actionable row — pending or ready first, then the
  // most recently paid. Total amount is the sum across what we surface.
  const featured = active[0] ?? recentPaid[0];
  if (!featured) return null;

  const headline =
    featured.status === "paid"
      ? "Prize sent · " + formatUsd(featured.amount_cents)
      : "🏆 You won " + formatUsd(featured.amount_cents) + "!";
  const sub =
    featured.status === "paid"
      ? "From " + featured.competition.name + ". Check your account."
      : featured.tax_info_status === "not_required"
        ? "Prize on the way — we'll be in touch with payment details."
        : featured.tax_info_status === "received"
          ? "Tax info received. Payment processing."
          : featured.tax_info_status === "requested"
            ? "Waiting on your tax info — check your email for the link."
            : "Tax info needed before we can send your prize.";

  return (
    <View>
      <SectionLabel title="Winnings" count={active.length + recentPaid.length} theme={theme} />
      <Pressable
        onPress={() => router.push("/student/winnings" as Href)}
        style={[
          {
            paddingHorizontal: 18,
            paddingVertical: 16,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: cardBorder,
            backgroundColor: cardBg,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
          },
          Platform.OS === "web"
            ? ({
                boxShadow: isDark
                  ? "0 2px 6px rgba(0,0,0,0.18)"
                  : "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
                cursor: "pointer",
              } as object)
            : null,
        ]}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: titleColor }}>
            {headline}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: subColor }}>
            {sub}
          </Text>
        </View>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: COLORS.AMBER_DEEP.hex,
          }}
        >
          Open →
        </Text>
      </Pressable>
    </View>
  );
}

function InterviewOffersTile({
  pendingCount,
  theme = "light",
}: {
  pendingCount: number;
  theme?: "light" | "dark";
}) {
  const isDark = theme === "dark";
  const titleColor = COLORS.FOREST.hex;
  const subColor = STUDENT_THEME.textOnSurfaceDim;
  const cardBg = COLORS.LINEN.hex;
  const cardBorder = COLORS.AMBER.hex;
  // Always render the tile when there's at least one pending offer; the
  // tile is the single most time-sensitive thing on the dashboard.
  if (pendingCount === 0) return null;
  return (
    <View>
      <SectionLabel title="Interview offers" count={pendingCount} theme={theme} />
      <Pressable
        onPress={() => router.push("/student/offers" as Href)}
        style={[
          {
            paddingHorizontal: 18,
            paddingVertical: 16,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: cardBorder,
            backgroundColor: cardBg,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
          },
          Platform.OS === "web"
            ? ({
                boxShadow: isDark
                  ? "0 2px 6px rgba(0,0,0,0.18)"
                  : "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
                cursor: "pointer",
              } as object)
            : null,
        ]}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: titleColor }}>
            {pendingCount} interview offer{pendingCount === 1 ? "" : "s"} waiting
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: subColor }}>
            Companies want to interview you. Respond when you're ready.
          </Text>
        </View>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: COLORS.AMBER_DEEP.hex,
          }}
        >
          Open →
        </Text>
      </Pressable>
    </View>
  );
}

function CareerInterestsTile({
  interests,
  theme = "light",
}: {
  interests: StudentCareerInterest[];
  theme?: "light" | "dark";
}) {
  const isDark = theme === "dark";
  const titleColor = COLORS.FOREST.hex;
  const subColor = STUDENT_THEME.textOnSurfaceDim;
  const cardBg = isDark ? COLORS.LINEN.hex : COLORS.WHITE.hex;
  const cardBorder = "rgba(28,43,26,0.10)";
  if (interests.length === 0) return null;
  const competitions = new Set(interests.map((i) => i.competition.id));
  const roleCount = interests.length;
  const compCount = competitions.size;
  return (
    <View>
      <SectionLabel title="Career interests" count={roleCount} theme={theme} />
      <Pressable
        onPress={() => router.push("/student/career-interests" as Href)}
        style={[
          {
            paddingHorizontal: 18,
            paddingVertical: 16,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: cardBorder,
            backgroundColor: cardBg,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
          },
          Platform.OS === "web"
            ? ({
                boxShadow: isDark
                  ? "0 2px 6px rgba(0,0,0,0.18)"
                  : "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
                cursor: "pointer",
              } as object)
            : null,
        ]}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: titleColor }}>
            {roleCount} role{roleCount === 1 ? "" : "s"} across {compCount} competition
            {compCount === 1 ? "" : "s"}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: subColor }}>
            Roles you've opted in for. Manage here.
          </Text>
        </View>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: COLORS.AMBER_DEEP.hex,
          }}
        >
          Manage →
        </Text>
      </Pressable>
    </View>
  );
}

/**
 * Header row above the My Programs cards. SectionLabel on the left,
 * "{N} active · {Season Year}" subtitle on the right (right side
 * omitted when there are no active cases). The season text is derived
 * from each case's `opens_at` — most cases will share a season, but
 * if they don't, we pick the soonest-opening case's season.
 */
function ProgramsHeaderRow({ cases }: { cases: StudentCaseRow[] }) {
  const sub = useMemo(() => {
    if (cases.length === 0) return undefined;
    const withOpens = cases
      .filter((c) => typeof c.opens_at === "string" && c.opens_at)
      .sort((a, b) => new Date(a.opens_at as string).getTime() - new Date(b.opens_at as string).getTime());
    const representative = withOpens[0] ?? cases[0];
    const opens = representative?.opens_at;
    if (!opens) return `${cases.length} active`;
    const d = new Date(opens);
    if (Number.isNaN(d.getTime())) return `${cases.length} active`;
    const month = d.getMonth();
    const season = month <= 3 ? "Spring" : month <= 7 ? "Summer" : "Fall";
    return `${cases.length} active · ${season} ${d.getFullYear()}`;
  }, [cases]);

  return (
    <SectionHeader
      theme={STUDENT_THEME}
      title="My programs"
      count={cases.length}
      subtitle={sub}
      tone="dark"
    />
  );
}

function SectionLabel({
  title,
  count,
  theme = "light",
}: {
  title: string;
  count?: number;
  theme?: "light" | "dark";
}) {
  const labelColor = theme === "dark" ? "rgba(247,244,238,0.62)" : STUDENT_THEME.textOnSurfaceDim;
  return (
    <View
      style={{
        marginBottom: 14,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 2.1,
          textTransform: "uppercase",
          color: labelColor,
        }}
      >
        {title}
      </Text>
      {count != null && count > 0 ? (
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 14,
            color: COLORS.AMBER.hex,
            letterSpacing: 0,
          }}
        >
          {count}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Editorial sidebar on /student's beige hero — DUE SOON eyebrow +
 * Cormorant title + up to 5 upcoming deliverable rows + "View all →"
 * footer. Filters to the next 14 days; anything past that drops off
 * until it's closer. Renders naked on beige (no card wrapper) — rows
 * separated only by 1px sand hairlines.
 */
function UpcomingDeliverablesCard({ deadlines }: { deadlines: DashboardDeadlineRow[] }) {
  const fourteenDayMs = 14 * 86400 * 1000;
  const now = Date.now();
  const filtered = deadlines.filter((d) => {
    const t = new Date(d.dueAt).getTime();
    if (!Number.isFinite(t)) return false;
    return t - now <= fourteenDayMs && t - now >= -3600 * 1000;
  });
  const visible = filtered.slice(0, 5);

  const items: DueSoonRailItem[] = visible.map((d) => {
    const dueMs = new Date(d.dueAt).getTime() - now;
    const daysUntil = Math.max(0, Math.floor(dueMs / 86400000));
    const date = formatDateShort(d.dueAt);
    const daysLeftPhrase =
      daysUntil === 0
        ? "Due today"
        : daysUntil === 1
        ? "1 day left"
        : daysUntil + " days left";
    return {
      key: `${d.competitionName}-${d.deliverableName}-${d.dueAt}`,
      daysUntil,
      primaryText: d.deliverableName,
      metaText: `${d.competitionName} · ${date} · ${daysLeftPhrase}`,
      onPress: d.competitionId
        ? () => {
            router.push(
              `/student/competition/${d.competitionId}?tab=deliverables` as Href
            );
          }
        : undefined,
      accessibilityLabel: `${d.deliverableName} — ${d.competitionName}, due ${date}, ${daysLeftPhrase}`,
    };
  });

  return (
    <DueSoonRail
      theme={STUDENT_THEME}
      items={items}
      totalCount={filtered.length}
      onPressViewAll={() => router.push("/student/submissions" as Href)}
    />
  );
}

/**
 * Editorial hero band — kicker + greeting + supporting copy + primary CTA.
 * Sand background, amber gradient hairline at the bottom edge.
 */
function DashboardHero({
  firstName,
  onFind,
  showGradMessage,
  daysUntilGraduation,
  onDismissGrad,
  deadlines,
  wide,
}: {
  firstName: string;
  onFind: () => void;
  showGradMessage: boolean;
  daysUntilGraduation: number | null;
  onDismissGrad: () => void;
  deadlines: DashboardDeadlineRow[];
  wide: boolean;
}) {
  const [dismissHover, setDismissHover] = useState(false);
  const isWeb = Platform.OS === "web";
  const greeting = useGreeting();
  return (
    <View
      style={{
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 28,
        backgroundColor: COLORS.OFFW.hex,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.SAND_DARK.hex,
        position: "relative",
        overflow: "hidden",
      }}
      className="md:px-11"
    >
      {/* Decorative palm branches — sage strokes on the cream hero.
          The bottom-left palm is offset so its fronds extend down past
          the seam, visually connecting to the green section's top-right
          palm below. */}
      <BrandScene scene="palms" tone="on-beige" />
      {/* Amber gradient hairline accent at bottom edge */}
      <View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            left: 24,
            right: 24,
            bottom: -1,
            height: 1,
          },
          isWeb
            ? ({
                backgroundImage:
                  "linear-gradient(90deg, transparent, rgba(181,135,58,0.45), transparent)",
              } as object)
            : { backgroundColor: "rgba(181,135,58,0.35)" },
        ]}
      />

      {/* Two-column hero — editorial copy on the left, Upcoming
          Deliverables editorial sidebar on the right. Stacks on mobile.
          Centered editorial container (max 1200px) so the layout feels
          intentional rather than stretched to viewport edges. Left
          column constrained so the gap between columns stays tight. */}
      <View
        style={{
          flexDirection: wide ? "row" : "column",
          gap: wide ? 40 : 32,
          alignItems: "flex-start",
          maxWidth: 1200,
          marginHorizontal: "auto",
          width: "100%",
        }}
      >
        {/* Left column — uses shared EditorialIntro aspect */}
        <View
          style={{
            minWidth: 0,
            flexBasis: 0,
            flexGrow: wide ? 1.4 : 1,
            flexShrink: 1,
            width: "100%",
          }}
        >
          <EditorialIntro
            theme={STUDENT_THEME}
            kicker="Coravu · Student"
            headline={{
              before: `${greeting}, `,
              accent: firstName || "there",
              after: ".",
            }}
            subtitle={"Here’s where things stand today — your active programs and what’s coming up next."}
            cta={{
              label: "Find a competition",
              onPress: onFind,
              accessibilityLabel: "Find a competition",
            }}
          >
            {showGradMessage && daysUntilGraduation !== null ? (
              <View
                style={{
                  position: "relative",
                  alignSelf: "flex-start",
                  paddingTop: 14,
                  paddingBottom: 14,
                  paddingLeft: 18,
                  paddingRight: 44,
                  marginBottom: 16,
                  maxWidth: 560,
                  borderLeftWidth: 3,
                  borderLeftColor: COLORS.AMBER.hex,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  backgroundColor: "rgba(181,135,58,0.06)",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.displayMediumItalic,
                    fontSize: 19,
                    lineHeight: 26,
                    color: COLORS.FOREST.hex,
                  }}
                >
                  Based on when you graduate, you have{" "}
                  <Text
                    style={{
                      color: COLORS.AMBER.hex,
                      fontFamily: FONTS.displayMediumItalic,
                      fontSize: 22,
                    }}
                  >
                    {daysUntilGraduation.toLocaleString()}
                  </Text>
                  {" "}days until you graduate.
                </Text>
                <Pressable
                  onPress={onDismissGrad}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss graduation message"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  {...(isWeb
                    ? ({ onMouseEnter: () => setDismissHover(true), onMouseLeave: () => setDismissHover(false) } as const)
                    : {})}
                  style={[
                    {
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 24,
                      height: 24,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 999,
                      borderWidth: 1,
                      borderColor: dismissHover ? COLORS.AMBER.hex : "rgba(28,43,26,0.18)",
                      backgroundColor: dismissHover ? "rgba(181,135,58,0.12)" : "transparent",
                    },
                    isWeb ? ({ cursor: "pointer", transition: "all 0.15s ease" } as object) : null,
                  ]}
                >
                  <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                    <Path
                      d="M2 2 L10 10 M10 2 L2 10"
                      stroke={dismissHover ? COLORS.AMBER.hex : COLORS.FOREST.hex}
                      strokeWidth={1.4}
                      strokeLinecap="round"
                    />
                  </Svg>
                </Pressable>
              </View>
            ) : null}
          </EditorialIntro>
        </View>

        {/* Right column — editorial Upcoming Deliverables sidebar on
            the beige bg. Sits naked (no card container) — just text
            and hairlines. */}
        <View
          style={{
            minWidth: 0,
            flexBasis: 0,
            flexGrow: 1,
            flexShrink: 1,
            width: "100%",
            maxWidth: wide ? 360 : undefined,
            paddingTop: wide ? 4 : 0,
          }}
        >
          <UpcomingDeliverablesCard deadlines={deadlines} />
        </View>
      </View>
    </View>
  );
}

function useGreeting() {
  return useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);
}

/** Maps `students.graduation_term` (Spring / Summer / Fall) to a typical
 *  US ceremony month/day. When no term is set we fall back to mid-May,
 *  which is the dominant graduation moment across US universities. */
function daysUntilGraduationDate(year: number, term: string | null): number | null {
  if (!Number.isFinite(year) || year < 1900 || year > 3000) return null;
  const t = (term ?? "").toLowerCase();
  let target: Date;
  if (t === "summer") target = new Date(year, 7, 10); // Aug 10
  else if (t === "fall") target = new Date(year, 11, 15); // Dec 15
  else target = new Date(year, 4, 15); // May 15 (Spring + unspecified)
  const ms = target.getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  // Past grad → return 0, not a negative count (the cursive copy reads
  // weirdly with "you have -5 days until you graduate").
  return Math.max(0, Math.ceil(ms / 86400000));
}
function DashboardSkeleton({ wide }: { wide: boolean }) {
  // Mirrors the loaded layout: BEIGE hero on top (with left-content +
  // Upcoming Deliverables sidebar columns), COLORS.FOREST.hex green section below
  // (with My Programs cards). Matching the two-tone split here prevents
  // the jarring "all beige → green appears" jump when data lands.
  //
  // Outer wrapper carries `backgroundColor: COLORS.FOREST.hex, flex: 1` so any
  // viewport space below the skeleton content stays forest-green —
  // without this, the parent shell's cream bg bleeds through and shows
  // a beige strip beneath the green section.
  return (
    <View style={{ backgroundColor: COLORS.FOREST.hex, flex: 1 }}>
      {/* BEIGE HERO — matches DashboardHero structure */}
      <View
        style={{
          backgroundColor: COLORS.OFFW.hex,
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 28,
          borderBottomWidth: 1,
          borderBottomColor: COLORS.SAND_DARK.hex,
          position: "relative",
          overflow: "hidden",
        }}
        className="md:px-11"
      >
        <BrandScene scene="palms" tone="on-beige" />
        <View
          style={{
            flexDirection: wide ? "row" : "column",
            gap: wide ? 40 : 32,
            alignItems: "flex-start",
            maxWidth: 1200,
            marginHorizontal: "auto",
            width: "100%",
          }}
        >
          {/* Hero left column skeleton */}
          <View style={{ minWidth: 0, flexBasis: 0, flexGrow: wide ? 1.4 : 1, flexShrink: 1, width: "100%" }}>
            <Skeleton width={120} height={9} />
            <Skeleton width={280} height={36} style={{ marginTop: 16 }} />
            <Skeleton width={"60%" as unknown as number} height={13} style={{ marginTop: 12 }} />
            <Skeleton width={"40%" as unknown as number} height={13} style={{ marginTop: 6 }} />
            <Skeleton width={"75%" as unknown as number} height={58} style={{ marginTop: 22 }} radius={4} />
            <Skeleton width={180} height={42} style={{ marginTop: 22 }} radius={6} />
          </View>
          {/* Hero right column — Upcoming Deliverables skeleton */}
          <View
            style={{
              minWidth: 0,
              flexBasis: 0,
              flexGrow: 1,
              flexShrink: 1,
              width: "100%",
              maxWidth: wide ? 360 : undefined,
              paddingTop: wide ? 4 : 0,
            }}
          >
            <Skeleton width={70} height={9} />
            <Skeleton width={200} height={22} style={{ marginTop: 6 }} />
            <Skeleton width={110} height={11} style={{ marginTop: 6 }} />
            <View style={{ marginTop: 12, gap: 14 }}>
              {[0, 1].map((k) => (
                <View key={k} style={{ flexDirection: "row", gap: 12 }}>
                  <Skeleton width={32} height={32} radius={999} />
                  <View style={{ flex: 1, gap: 6 }}>
                    <Skeleton width={"70%" as unknown as number} height={13} />
                    <Skeleton width={"90%" as unknown as number} height={11} />
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      {/* COLORS.FOREST.hex GREEN section — matches the My Programs container */}
      <View
        style={{
          backgroundColor: COLORS.FOREST.hex,
          paddingBottom: 60,
          minHeight: 600,
          flexGrow: 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <BrandScene scene="palms" tone="on-green" />
        <View className="max-w-[1200px] mx-auto px-5 pt-7 pb-6 md:px-11">
          {/* Section header row */}
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <Skeleton width={140} height={11} />
            <Skeleton width={120} height={11} />
          </View>
          {/* 2-column program card grid skeleton */}
          <View style={{ flexDirection: wide ? "row" : "column", gap: 16, flexWrap: "wrap" }}>
            {[0, 1].map((k) => (
              <View
                key={k}
                style={{
                  flex: wide ? 1 : undefined,
                  minWidth: wide ? 320 : undefined,
                  width: "100%",
                  backgroundColor: COLORS.LINEN.hex,
                  borderColor: "rgba(28,43,26,0.10)",
                  borderWidth: 1,
                  borderRadius: 8,
                  paddingHorizontal: 22,
                  paddingVertical: 20,
                }}
              >
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                  <Skeleton width={180} height={10} />
                  <Skeleton width={84} height={20} radius={999} />
                </View>
                <Skeleton width={"80%" as unknown as number} height={26} style={{ marginTop: 14 }} />
                <Skeleton width={"55%" as unknown as number} height={12} style={{ marginTop: 12 }} />
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 20 }}>
                  <Skeleton width={60} height={10} />
                  <Skeleton width={120} height={12} />
                </View>
                <View style={{ flexDirection: "row", gap: 4, marginTop: 6 }}>
                  {[0, 1, 2, 3, 4].map((s) => (
                    <View key={s} style={{ flex: 1 }}>
                      <Skeleton width={"100%" as unknown as number} height={8} radius={4} />
                    </View>
                  ))}
                </View>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 18 }}>
                  <Skeleton width={140} height={12} />
                  <Skeleton width={60} height={10} />
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}
